import { MEDIA_QUERIES } from "@constants"
import styled, { keyframes } from "styled-components"

const FadeInAnim = keyframes`
  0% {
    opacity: 0;
  }

  100% {
    opacity: 1;
  }
`

export const PricingHeroHERO = styled.div`
  --PricingHero--plans-gap: 12px;
  --PricingHeroPlan--card-min-width: 187px;
  --PricingHeroPlan--card-pct-width: calc(33.3333% - var(--PricingHero--plans-gap) * 2 / 3);
  --PricingHeroPlan--card-width: max(var(--PricingHeroPlan--card-min-width), var(--PricingHeroPlan--card-pct-width));
  --PricingHero--pricing-overlap:
    // padding-bottom of hero +
    // height of ProPlan card +
    // padding of ProPlan card +
    // gap between plan cards & ProPlan card +
    // padding-bottom of PricingHeroPlan +
    // .Button.size--M height
    calc(
      var(--spacing--XL) +
      var(--font-size--S) * var(--line-height--relaxed) +
      var(--spacing--base-1-5) * 2 +
      var(--PricingHero--plans-gap) +
      var(--spacing--base-2) +
      var(--button--height-M)
    );

  overflow: hidden;
  padding: calc(var(--SiteNav--height) + var(--spacing--M)) 0 0;
  position: relative;

  &.is-sales-page {
    --PricingHero--pricing-overlap:
      // padding-bottom of hero +
      // height of ProPlan card +
      // padding of ProPlan card +
      // gap between plan cards & ProPlan card +
      // padding-bottom of PricingHeroPlan
      calc(
        var(--spacing--XL) +
        var(--font-size--S) * var(--line-height--relaxed) +
        var(--spacing--base-1-5) * 2 +
        var(--PricingHero--plans-gap) +
        var(--spacing--base-2)
      );
  }

  &::before {
    background: var(--color--orange-100);
    content: "";
    display: block;
    height: calc(100% - var(--PricingHero--pricing-overlap));
    left: 0;
    position: absolute;
    top: 0;
    width: 100%;
    z-index: -1;
  }

  h1 {
    margin-bottom: calc(var(--spacing--S) + var(--spacing--XS));
    text-align: center;

    span {
      display: block;
      margin: 0;
    }

    ${MEDIA_QUERIES.L} {
      text-align: left;
    }
  }

  .grid {
    width: 100%;
  }

  .contacts-slider {
    display: flex;
    gap: var(--spacing--S);
    justify-content: space-between;
    padding: 0 0 var(--spacing--base-1);
    position: relative;
    flex-direction: column-reverse;
    flex-wrap: wrap;

    ${MEDIA_QUERIES.L} {
      justify-content: flex-start;
      flex-direction: column;
      gap: var(--spacing--M);
    }
  }

  .contacts-slider--content {
    position: relative;

    ${MEDIA_QUERIES.M} {
      flex-direction: row;
    }

    .gatsby-image-wrapper {
      &.desktop {
        display: none !important;
        flex: 0 0 50px;
      }

      ${MEDIA_QUERIES.M} {
        &.desktop {
          display: block !important;
        }

        &.mobile {
          display: none !important;
        }
      }
    }

    .current-increment {
      line-height: 28px;
      margin: 0 0 var(--spacing--S);
      white-space: nowrap;

      ${MEDIA_QUERIES.L} {
        display: flex;
        flex-direction: column-reverse;
        gap: 4px;
        line-height: var(--line-height--tight);

        span:first-child {
          font-size: var(--font-size--5XL);
          font-weight: var(--font-weight--medium);
        }

        span:last-child {
          text-transform: capitalize;
        }
      }
    }
  }

  .controls {
    width: 100%;
    display: flex;
    flex-direction: column;
    gap: 4px;

    ${MEDIA_QUERIES.M} {
      flex: 1;
    }
  }

  .range-input-wrapper {
    align-items: center;
    display: flex;
    margin-bottom: var(--spacing--XS);

    ${MEDIA_QUERIES.L} {
      margin-bottom: 4px;
    }
  }

  .range-legend {
    display: none;

    ${MEDIA_QUERIES.L} {
      display: flex;
      font-weight: var(--font-weight--semibold);
      justify-content: space-between;
      margin-bottom: var(--spacing--XS);
    }
  }

  .range-input--track {
    background: linear-gradient(to right, var(--color--orange-200), var(--color--orange-100));
    border-radius: 4px;
    border: 1px solid var(--color--orange-300);
    height: 8px;
    width: 100%;
    position: relative;

    &::before {
      background: var(--color--action-default);
      border-radius: 4px;
      content: "";
      display: block;
      height: calc(100% + 2px);
      left: 0;
      position: absolute;
      top: -1px;
      width: var(--Range--indicator-width);
    }
  }

  .range-input--thumb {
    animation: 0.3s 2s ${FadeInAnim} forwards;
    cursor: grab;
    opacity: 0;
    outline: 0;
    padding: 10px;
    position: absolute;
    text-align: center;
    touch-action: none;
    transform: translate(-20px, -16px);
    user-select: none;

    &::after {
      content: "";
      display: block;
      background: var(--color--basics-white);
      width: 20px;
      height: 20px;
      border-radius: 50%;
      border: 2px solid var(--color--action-default);
    }

    div {
      position: absolute;
      bottom: 24px;
      left: 50%;
      translate: -50% 0;
      background: var(--color--charcoal-1200);
      border-radius: 100px;
      box-shadow: var(--elevation--S);
      color: var(--color--basics-white);
      padding: 2px 9px;
      min-width: 57px;

      &::after {
        content: "";
        display: block;
        position: absolute;
        top: 100%;
        left: calc(50% - 5px);
        width: 0;
        height: 0;
        border: 4.5px solid transparent;
        border-top-color: var(--color--charcoal-1200);
        border-bottom: none;
      }
    }
  }

  .toggle-wrapper {
    align-items: center;
    display: flex;
    gap: var(--border-radius--S);
    justify-content: center;
    position: relative;

    ${MEDIA_QUERIES.L} {
      justify-content: flex-start;
    }

    > div {
      position: relative;
    }

    .ToggleWithText {
      margin: 0;
    }

    .annual-discount-badge {
      background: var(--color--green-100);
      border-radius: var(--border-radius--XS);
      border: 1px solid var(--color--green-300);
      box-shadow: 0px 0px 4px 0px rgba(var(--color--green-accent-rgb), 0.50);
      display: block;
      opacity: 0;
      padding: 0 6px;
      position: absolute;
      right: 0;
      top: 50%;
      transition: opacity 0.5s, translate 0.3s;
      translate: calc(100% + var(--spacing--XS) / 2) -50%;

      &.is-visible {
        opacity: 1;
        translate: calc(100% + var(--spacing--XS)) -50%;
      }
    }
  }

  .ticket-count {
    font-style: italic;
    text-align: center;

    em {
      font-style: normal;
    }

    span {
      background: var(--color--orange-200);
      border-radius: 1000px;
      display: inline-block;
      font-style: normal;
      font-weight: var(--font-weight--bold);
      padding: 0 10px;
      margin-right: 0.3em;
    }

    ${MEDIA_QUERIES.L} {
      text-align: left;
      margin-bottom: var(--spacing--S);
    }
  }

  .pricing {
    position: relative;
    z-index: 1;
    display: flex;
    flex-wrap: wrap;
    gap: var(--PricingHero--plans-gap);
    padding: 0 0 var(--spacing--XL);

    > div {
      position: relative;
    }

    .flex {
      flex-wrap: nowrap;
      gap: 0 var(--PricingHero--plans-gap);
      min-width: 100%;
      transform-origin: center center;
      transition: translate 0.3s ease-out;
      width: fit-content;

      @media (min-width: 635px) {
        transition: none;
        translate: 0 !important;
      }
    }
  }

  .ProPlan {
    --Plan--gradient-color: var(--color--purple-100-rgb);
    --Plan--radial-gradient-color: var(--color--purple-400-rgb);
    --Plan--border-padding: 6px;
    --Plan--gradient-border-radius: calc(var(--border-radius--L) * 0.4);
    
    align-items: center;
    background: var(--color--basics-white);
    border-radius: var(--border-radius--L);
    border: 1px solid rgba(var(--color--cobalt-900-rgb), 0.08);
    box-shadow: var(--elevation--S);
    display: flex;
    flex: 0 0 100%;
    gap: var(--spacing--S);
    justify-content: space-between;
    padding: var(--spacing--base-1-5) var(--spacing--base-2);
    position: relative;
    transition: all 0.2s;

    > * {
      position: relative;
      z-index: 1;
      margin: 0;
    }

    p {
      text-wrap: balance;
      text-align: center;

      span {
        display: none;

        ${MEDIA_QUERIES.M} {
          display: inline;
        }
      }
    }
    
    .gradient-background {
      border-radius: var(--Plan--gradient-border-radius);
      height: calc(100% - var(--Plan--border-padding) * 2);
      left: var(--Plan--border-padding);
      opacity: 0.3;
      overflow: hidden;
      position: absolute;
      top: var(--Plan--border-padding);
      width: calc(100% - var(--Plan--border-padding) * 2);
      z-index: 0;

      &::before,
      &::after {
        content: "";
        display: block;
        background:
          url("/images/noise-overlay.png"),
          radial-gradient(circle at 100% 0%, rgba(var(--Plan--radial-gradient-color), 0.45) 0%, rgba(var(--Plan--radial-gradient-color), 0) 33.3333%),
          rgba(var(--Plan--gradient-color), 1);
        height: 400%;
        width: 100%;
        position: absolute;
        top: 0;
        left: 0;
      }

      &::before {
        border: 0.5px solid rgba(var(--color--cobalt-900-rgb), 0.05);
        z-index: 1;
      }

      &::after {
        background: linear-gradient(180deg, rgba(var(--color--basics-white-rgb), 0), rgba(var(--color--basics-white-rgb), 1));
        z-index: 2;
      }
    }
  }
`
